import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ServiceType } from '@/shared/api/protocol-ts/api/billing_new/dto_services_new_pb';
import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { ENVIRONMENT } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { getName } from '@/shared/lib';
import { Modal, Select, Switch } from '@/shared/ui';

import {
  ASSET_TO_SERVICE_ORDER_REPORT_MAP,
  AssetToServiceOrderReportMap,
  billingModel,
} from '@/entities/billing';
import { useDiagnocoinFunding } from '@/entities/composed';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { userModel } from '@/entities/user';

import { useNewReport } from '@/features/addNewReport';
import { useUploadAssetContext } from '@/features/uploadAsset';

import {
  UPLOAD_STUDY_FORM_ID,
  UploadStudyAssetType,
  UploadStudyPayload,
  uploadStudySchema,
} from '../../config';
import { uploadStudyHelpText } from '../../config/i18n';
import { useSelectStudyOptions } from '../../hooks';
import { areFilesValid } from '../../utils/areFilesValid';
import { isExceedTheSize } from '../../utils/isExceedTheSize';
import { shouldShowCreateReportToggle } from '../../utils/shouldShowCreateReportToggle';
import { CreateReportToggleText } from '../CreateReportToggleText/CreateReportToggleText';
import { UploadStudyDropzone } from '../UploadStudyDropzone/UploadStudyDropzone';
import { UploadStudyFile } from '../UploadStudyFile/UploadStudyFile';

import styles from './UploadStudyModalForm.module.scss';

export const UploadStudyModalForm = () => {
  const dispatch = useAppDispatch();

  const { formatMessage } = useIntl();
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [uploadFilesError, setUploadFilesError] = useState('');

  const {
    visible,
    data: { patientID },
  } = useAppSelector(modalModel.selectors.selectUploadModal);

  const { isFDA, isFDAVersion2 } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );

  const services = useAppSelector(
    billingModel.selectors.selectActiveServicesList,
  );

  const { startUploadAsset: startUploadStudy } = useUploadAssetContext();

  const { requestCBCTGPReport, requestIOXRayGPReport, requestPanoGPReport } =
    useNewReport();

  const patient = useAppSelector(
    patientModel.selectors.selectPatientByID(patientID as string),
  );
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);
  const patientName = getName(
    patient?.PersonalData?.FirstName,
    patient?.PersonalData?.LastName,
    { userLocale },
  );

  const {
    CanUploadCBCT,
    CanUploadDentalPhoto,
    CanUploadPanoramicXRay,
    CanUploadIOXRay,
  } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );

  const studyOptions = useSelectStudyOptions();

  // Legacy FDA is CBCT-only; FDA 2.0 also supports 2D modalities (FMX, Pano).
  const fdaAllowedAssetTypes = isFDAVersion2
    ? [
        AssetType.AssetType_Study_CBCT,
        AssetType.AssetType_Study_PanoramicXRay,
        AssetType.AssetType_Study_IntraoralXRay,
      ]
    : [AssetType.AssetType_Study_CBCT];

  // TODO: [4|h] move all computed data to selectors
  const optionsFilteredByBilling = studyOptions.filter(({ value }) => {
    if (isFDA && !fdaAllowedAssetTypes.includes(value)) {
      return false;
    }

    switch (value) {
      // WA-6209: uploads are no longer metered/entitled, so upload availability
      // is just the user's upload permission; the per-modality payment counters
      // were removed from the protocol.
      case AssetType.AssetType_Study_CBCT:
        return CanUploadCBCT;

      case AssetType.AssetType_Study_IntraoralXRay:
        return CanUploadIOXRay;

      case AssetType.AssetType_Study_PanoramicXRay:
        return CanUploadPanoramicXRay;

      case AssetType.AssetType_Study_IOS_Meshes:
        return (
          // TODO: this is hack for WA-3217
          ENVIRONMENT === 'development' && CanUploadDentalPhoto
        );

      default:
        return false;
    }
  });

  const isSegmentronActive =
    services[ServiceType.ServiceType_Order_ImplantStudio_Analysis] ||
    services[ServiceType.ServiceType_Order_OrthoStudio_Analysis] ||
    services[ServiceType.ServiceType_Order_CBCT_IOS_Superimposition_Analysis] ||
    services[ServiceType.ServiceType_Order_IOS_Segmentation_Analysis] ||
    services[ServiceType.ServiceType_Order_CBCT_STL_Analysis];

  const optionsUpdatedBySegmentron = isSegmentronActive
    ? optionsFilteredByBilling
    : optionsFilteredByBilling.filter(
        (studyOption) =>
          studyOption.value !== AssetType.AssetType_Study_IOS_Meshes,
      );

  const uploadFolderInputElement = document.querySelector(
    `#uploadFolderInput`,
  ) as HTMLInputElement;

  const {
    watch,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<UploadStudyPayload>({
    defaultValues: {
      studyAssetType: AssetType.AssetType_Study_CBCT,
      createReportAfterLoading: true,
    },
    resolver: yupResolver(uploadStudySchema),
  });

  const currentStudyAssetType = watch('studyAssetType');

  const autoOrderServiceTypes = useMemo(() => {
    const serviceType =
      ASSET_TO_SERVICE_ORDER_REPORT_MAP[
        currentStudyAssetType as keyof AssetToServiceOrderReportMap
      ];

    return serviceType === undefined ? [] : [serviceType];
  }, [currentStudyAssetType]);

  const autoOrderFunding = useDiagnocoinFunding(autoOrderServiceTypes);

  const availableReportsCount =
    services[
      ASSET_TO_SERVICE_ORDER_REPORT_MAP[
        currentStudyAssetType as keyof AssetToServiceOrderReportMap
      ]
    ];

  const addFiles = (accepterFiles: FileWithPath[]) => {
    const validatedFiles = accepterFiles.filter((file) =>
      areFilesValid(currentStudyAssetType, [file]),
    );

    setFiles((currentFiles) => [...currentFiles, ...validatedFiles]);
  };

  const removeAllFiles = () => {
    if (uploadFolderInputElement?.value) {
      uploadFolderInputElement.value = '';
    }
    setFiles([]);
  };

  const dropzoneState = useDropzone({
    onDrop: addFiles,
    disabled: !currentStudyAssetType,
  });

  const handleClose = useCallback(() => {
    dispatch(modalModel.actions.closeModal(ModalID.UploadStudy_DEPRECATED));

    removeAllFiles();
  }, []);

  const onSubmit: SubmitHandler<UploadStudyPayload> = async (data) => {
    handleClose();

    const { createReportAfterLoading, studyAssetType } = data;

    const { studyID } = await startUploadStudy({
      patientID,
      files,
      assetType: studyAssetType,
      meta: {
        studyType: studyAssetType,
        patientName,
      },
    });

    if (studyID && createReportAfterLoading) {
      switch (studyAssetType) {
        case AssetType.AssetType_Study_CBCT: {
          requestCBCTGPReport(studyID);
          break;
        }
        case AssetType.AssetType_Study_IntraoralXRay: {
          requestIOXRayGPReport(studyID);
          break;
        }
        case AssetType.AssetType_Study_PanoramicXRay: {
          requestPanoGPReport(studyID);
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  const showCreateReportToggle = shouldShowCreateReportToggle(
    currentStudyAssetType,
  );

  useEffect(() => {
    if (
      !files.length ||
      (!isExceedTheSize(files, currentStudyAssetType) &&
        areFilesValid(currentStudyAssetType, files))
    ) {
      setUploadFilesError('');
      return;
    }

    if (isExceedTheSize(files, currentStudyAssetType)) {
      const maxSize =
        currentStudyAssetType === AssetType.AssetType_Study_CBCT
          ? '10GB'
          : '250MB';

      setUploadFilesError(
        formatMessage(
          {
            id: 'uploadStudyModalForm.sizeError',
            defaultMessage: 'File(s) size must be less than {maxSize}',
          },
          { maxSize },
        ),
      );
    }

    if (!areFilesValid(currentStudyAssetType, files)) {
      setUploadFilesError(
        formatMessage({
          id: 'uploadStudyModalForm.wrongFile',
          defaultMessage: 'Wrong File(s) format',
        }),
      );
    }
  }, [files, currentStudyAssetType]);

  return (
    <Modal
      data-component="upload-study-modal"
      containerClassName={styles.modalcontainer}
      title={
        <FormattedMessage
          id="uploadStudyModalForm.title"
          defaultMessage="Upload study"
        />
      }
      isOpen={visible}
      onCancel={handleClose}
      borderless
      okButtonText={
        <FormattedMessage
          id="uploadStudyModalForm.applyButtonText"
          defaultMessage="Upload"
        />
      }
      okButtonProps={{
        type: 'submit',
        form: UPLOAD_STUDY_FORM_ID,
        disabled: files.length === 0 || !!uploadFilesError,
      }}
    >
      <form
        id={UPLOAD_STUDY_FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        className={styles.container}
      >
        <Controller
          control={control}
          name="studyAssetType"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              label={
                <FormattedMessage
                  id="uploadStudyModalForm.studyTypeLabel"
                  defaultMessage="Select study type"
                />
              }
              isSearchable={false}
              options={optionsUpdatedBySegmentron}
              value={value}
              onChange={onChange}
              isRequired
              error={error?.message}
            />
          )}
        />

        {files.length > 0 && (
          <UploadStudyFile
            files={files}
            onDelete={removeAllFiles}
            errorText={uploadFilesError}
          />
        )}

        {currentStudyAssetType && (
          <p className={cn(styles.helpText, 'p3')}>
            {formatMessage(
              uploadStudyHelpText[
                currentStudyAssetType as UploadStudyAssetType
              ],
            )}
          </p>
        )}

        <UploadStudyDropzone
          canUploadFolder={
            currentStudyAssetType !== AssetType.AssetType_Study_PanoramicXRay
          }
          dropzoneState={dropzoneState}
        />

        {showCreateReportToggle && (
          <Controller
            control={control}
            name="createReportAfterLoading"
            render={({ field: { value = false, onChange } }) => (
              <Switch
                className={styles.reportSwitch}
                checked={value}
                disabled={!availableReportsCount || isSubmitting}
                onChange={onChange}
              >
                <CreateReportToggleText
                  studyAssetType={currentStudyAssetType}
                  coins={
                    autoOrderFunding.source === 'diagnocoins'
                      ? autoOrderFunding.coins
                      : undefined
                  }
                />
                <p className={cn(styles.reportsCounter, 'p3')}>
                  <FormattedMessage
                    id="uploadStudyModalForm.reportsCount"
                    defaultMessage="{ availableReportsCount } reports left"
                    values={{ availableReportsCount }}
                  />
                </p>
              </Switch>
            )}
          />
        )}
      </form>
    </Modal>
  );
};
